import React, { useState, useCallback, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  RefreshControl,
} from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedPanel } from "@/components/ThemedPanel";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@components/firebase/useFirebase";
import { getAllWorkflows } from "@/services/firebase/workflow-functions";
import { WorkflowModel, TaskModel, WorkflowState } from "common247";

/**
 * State filter choices available in the admin tracker.
 */
type FilterState = "all" | WorkflowState;

/**
 * Administrative screen enabling platform admins to inspect, search,
 * and track all workflow instances and their tasks across the system,
 * displaying associated AppUsers assigned to action them.
 */
export default function WorkflowAdminScreen() {
  const services = useFirebase();
  const { user } = useAuth();

  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const cardBg = useThemeColor({}, "cardBackground");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");

  const [workflows, setWorkflows] = useState<WorkflowModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<FilterState>("all");
  const [expandedWorkflowIds, setExpandedWorkflowIds] = useState<Set<string>>(new Set());

  // Check admin role
  const isAdmin = useMemo(() => {
    if (!user) return false;
    const claims = (user as any).claims || (user as any).tokenClaims || {};
    return (
      claims.admin === true ||
      claims.admin === "*" ||
      (Array.isArray(claims.admin) && claims.admin.length > 0) ||
      (typeof claims.admin === "string" && claims.admin.length > 0)
    );
  }, [user]);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (!services) return;
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await getAllWorkflows(services, {});
        if (response.status) {
          setWorkflows(response.workflows || []);
        } else {
          CustomAlert.alert("Error", response.error || "Failed to load workflows.");
        }
      } catch (error: any) {
        console.error("Error fetching admin workflows:", error);
        CustomAlert.alert("Error", error.message || "An unexpected error occurred while loading workflows.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [services]
  );

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const toggleExpand = (workflowId: string) => {
    setExpandedWorkflowIds((prev) => {
      const next = new Set(prev);
      if (next.has(workflowId)) {
        next.delete(workflowId);
      } else {
        next.add(workflowId);
      }
      return next;
    });
  };

  // Metrics aggregation
  const metrics = useMemo(() => {
    let pendingTasks = 0;
    let completedWfs = 0;
    let rejectedWfs = 0;

    for (const w of workflows) {
      if (w.currentState === "completed") completedWfs++;
      if (w.currentState === "rejected") rejectedWfs++;
      if (Array.isArray(w.tasks)) {
        for (const t of w.tasks) {
          if (t.status === "pending") pendingTasks++;
        }
      }
    }

    return {
      total: workflows.length,
      pendingTasks,
      completed: completedWfs,
      rejected: rejectedWfs,
    };
  }, [workflows]);

  // Filtered & searched workflows
  const filteredWorkflows = useMemo(() => {
    return workflows.filter((w) => {
      // Status filter
      if (selectedFilter !== "all" && w.currentState !== selectedFilter) {
        return false;
      }

      // Search query
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = w.name?.toLowerCase().includes(q);
        const matchesMemory = w.memoryTitle?.toLowerCase().includes(q);
        const matchesOwner =
          w.ownerDisplayName?.toLowerCase().includes(q) || w.ownerEmail?.toLowerCase().includes(q);
        const matchesAssignee =
          w.assigneeDisplayName?.toLowerCase().includes(q) || w.assigneeEmail?.toLowerCase().includes(q);
        const matchesTasks = (w.tasks || []).some(
          (t) =>
            t.title?.toLowerCase().includes(q) ||
            t.description?.toLowerCase().includes(q) ||
            t.assigneeDisplayName?.toLowerCase().includes(q) ||
            t.assigneeEmail?.toLowerCase().includes(q)
        );

        return matchesName || matchesMemory || matchesOwner || matchesAssignee || matchesTasks;
      }

      return true;
    });
  }, [workflows, selectedFilter, searchQuery]);

  const getStateBadgeStyle = (state: WorkflowState) => {
    switch (state) {
      case "pending_approval":
        return { bg: "#FEF3C7", text: "#D97706", label: "Pending Approval" };
      case "completed":
        return { bg: "#DEF7EC", text: "#046C4E", label: "Completed" };
      case "rejected":
        return { bg: "#FDE8E8", text: "#C81E1E", label: "Rejected" };
      case "draft":
      default:
        return { bg: "#E5E7EB", text: "#374151", label: "Draft" };
    }
  };

  const getTaskStatusBadgeStyle = (status: TaskModel["status"]) => {
    switch (status) {
      case "pending":
        return { bg: "#EFF6FF", text: "#1D4ED8", label: "Action Pending" };
      case "accepted":
        return { bg: "#DEF7EC", text: "#046C4E", label: "Accepted" };
      case "rejected":
        return { bg: "#FDE8E8", text: "#C81E1E", label: "Rejected" };
      default:
        return { bg: "#F3F4F6", text: "#4B5563", label: status };
    }
  };

  if (!isAdmin && !loading) {
    return (
      <ThemedScreen title="All Workflows" scrollable={false}>
        <ThemedView style={styles.centerContainer}>
          <ThemedText style={styles.lockIcon}>🔒</ThemedText>
          <ThemedText type="subtitle" style={styles.accessDeniedTitle}>
            Administrator Access Required
          </ThemedText>
          <ThemedText style={[styles.accessDeniedText, { color: textMuted }]}>
            This screen is restricted to platform administrators. Your account does not currently hold administrative permissions.
          </ThemedText>
        </ThemedView>
      </ThemedScreen>
    );
  }

  return (
    <ThemedScreen title="All Workflows" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        {/* Metric summary badges */}
        <View style={styles.metricsRow}>
          <ThemedPanel style={[styles.metricCard, { borderColor }]}>
            <ThemedText type="stamp" style={{ color: textMuted }}>TOTAL WORKFLOWS</ThemedText>
            <ThemedText style={styles.metricNumber}>{metrics.total}</ThemedText>
          </ThemedPanel>
          <ThemedPanel style={[styles.metricCard, { borderColor }]}>
            <ThemedText type="stamp" style={{ color: "#D97706" }}>PENDING TASKS</ThemedText>
            <ThemedText style={[styles.metricNumber, { color: "#D97706" }]}>{metrics.pendingTasks}</ThemedText>
          </ThemedPanel>
          <ThemedPanel style={[styles.metricCard, { borderColor }]}>
            <ThemedText type="stamp" style={{ color: "#046C4E" }}>COMPLETED</ThemedText>
            <ThemedText style={[styles.metricNumber, { color: "#046C4E" }]}>{metrics.completed}</ThemedText>
          </ThemedPanel>
          <ThemedPanel style={[styles.metricCard, { borderColor }]}>
            <ThemedText type="stamp" style={{ color: "#C81E1E" }}>REJECTED</ThemedText>
            <ThemedText style={[styles.metricNumber, { color: "#C81E1E" }]}>{metrics.rejected}</ThemedText>
          </ThemedPanel>
        </View>

        {/* Search bar */}
        <View style={[styles.searchContainer, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText style={styles.searchIcon}>🔍</ThemedText>
          <TextInput
            style={[styles.searchInput, { color: textColor }]}
            placeholder="Search workflows, tasks, memories, or users..."
            placeholderTextColor={textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
            clearButtonMode="while-editing"
          />
        </View>

        {/* State filter buttons */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersScroll}>
          {(
            [
              { key: "all", label: "All" },
              { key: "pending_approval", label: "Pending Approval" },
              { key: "draft", label: "Drafts" },
              { key: "completed", label: "Completed" },
              { key: "rejected", label: "Rejected" },
            ] as const
          ).map((item) => {
            const isSelected = selectedFilter === item.key;
            return (
              <TouchableOpacity
                key={item.key}
                onPress={() => setSelectedFilter(item.key)}
                style={[
                  styles.filterPill,
                  { borderColor },
                  isSelected && { backgroundColor: tintColor, borderColor: tintColor },
                ]}
              >
                <ThemedText
                  style={[
                    styles.filterPillText,
                    { color: isSelected ? "#FFFFFF" : textColor },
                    isSelected && { fontWeight: "700" },
                  ]}
                >
                  {item.label}
                </ThemedText>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* Workflow List */}
        <ScrollView
          style={styles.listContainer}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => fetchData(true)} />}
        >
          {filteredWorkflows.length === 0 ? (
            <View style={styles.emptyContainer}>
              <ThemedText style={{ color: textMuted, textAlign: "center", marginTop: 40 }}>
                No workflows match your criteria.
              </ThemedText>
            </View>
          ) : (
            filteredWorkflows.map((workflow) => {
              const wfId = workflow.id || "wf-unknown";
              const isExpanded = expandedWorkflowIds.has(wfId);
              const stateBadge = getStateBadgeStyle(workflow.currentState);
              const tasks = workflow.tasks || [];

              return (
                <ThemedPanel key={wfId} style={[styles.workflowCard, { borderColor }]}>
                  {/* Workflow Card Header */}
                  <View style={styles.wfHeaderRow}>
                    <View style={styles.wfTitleContainer}>
                      <ThemedText type="subtitle" style={styles.wfTitle}>
                        {workflow.name}
                      </ThemedText>
                      {workflow.memoryTitle ? (
                        <ThemedText style={[styles.wfMemoryText, { color: textMuted }]}>
                          🧠 {workflow.memoryTitle}
                        </ThemedText>
                      ) : null}
                    </View>
                    <View style={[styles.badgePill, { backgroundColor: stateBadge.bg }]}>
                      <ThemedText style={[styles.badgeText, { color: stateBadge.text }]}>
                        {stateBadge.label}
                      </ThemedText>
                    </View>
                  </View>

                  {/* Users involved info */}
                  <View style={styles.usersInfoSection}>
                    <View style={styles.userRow}>
                      <ThemedText style={[styles.userLabel, { color: textMuted }]}>Creator / Owner:</ThemedText>
                      <ThemedText style={styles.userValue}>
                        👤 {workflow.ownerDisplayName || "Unknown"}
                        {workflow.ownerEmail ? ` (${workflow.ownerEmail})` : ""}
                      </ThemedText>
                    </View>
                    <View style={styles.userRow}>
                      <ThemedText style={[styles.userLabel, { color: textMuted }]}>Current Assignee:</ThemedText>
                      <ThemedText style={styles.userValue}>
                        👉 {workflow.assigneeDisplayName || "Unassigned"}
                        {workflow.assigneeEmail ? ` (${workflow.assigneeEmail})` : ""}
                      </ThemedText>
                    </View>
                  </View>

                  {/* Tasks breakdown accordion header */}
                  <TouchableOpacity
                    onPress={() => toggleExpand(wfId)}
                    style={[styles.taskAccordionToggle, { borderTopColor: borderColor }]}
                    activeOpacity={0.7}
                  >
                    <ThemedText style={[styles.taskCountText, { color: tintColor }]}>
                      📋 Associated Tasks ({tasks.length})
                    </ThemedText>
                    <ThemedText style={{ color: tintColor, fontSize: 13 }}>
                      {isExpanded ? "▲ Hide Tasks" : "▼ Show Tasks"}
                    </ThemedText>
                  </TouchableOpacity>

                  {/* Tasks List */}
                  {isExpanded && (
                    <View style={styles.tasksListContainer}>
                      {tasks.length === 0 ? (
                        <ThemedText style={[styles.noTasksText, { color: textMuted }]}>
                          No task records associated with this workflow.
                        </ThemedText>
                      ) : (
                        tasks.map((task) => {
                          const statusBadge = getTaskStatusBadgeStyle(task.status);
                          return (
                            <View
                              key={task.id || `task-${task.createdAt}`}
                              style={[styles.taskItemCard, { borderColor }]}
                            >
                              <View style={styles.taskItemHeader}>
                                <ThemedText style={styles.taskTitle}>{task.title}</ThemedText>
                                <View style={[styles.badgePillSmall, { backgroundColor: statusBadge.bg }]}>
                                  <ThemedText style={[styles.badgeTextSmall, { color: statusBadge.text }]}>
                                    {statusBadge.label}
                                  </ThemedText>
                                </View>
                              </View>

                              {task.description ? (
                                <ThemedText style={[styles.taskDescription, { color: textMuted }]}>
                                  {task.description}
                                </ThemedText>
                              ) : null}

                              {/* AppUser who needs to action the task */}
                              <View style={[styles.actionUserBox, { backgroundColor: cardBg, borderColor }]}>
                                <ThemedText style={[styles.actionUserLabel, { color: textMuted }]}>
                                  Assigned AppUser to Action:
                                </ThemedText>
                                <ThemedText style={styles.actionUserName}>
                                  👤 {task.assigneeDisplayName || task.assignedTo}
                                  {task.assigneeEmail ? ` • ${task.assigneeEmail}` : ""}
                                </ThemedText>
                              </View>

                              {/* Timestamps */}
                              <View style={styles.taskMetaRow}>
                                <ThemedText style={[styles.taskMetaText, { color: textMuted }]}>
                                  Created: {new Date(task.createdAt).toLocaleDateString()}
                                </ThemedText>
                                {task.completedAt ? (
                                  <ThemedText style={[styles.taskMetaText, { color: textMuted }]}>
                                    Completed: {new Date(task.completedAt).toLocaleDateString()}
                                  </ThemedText>
                                ) : null}
                              </View>
                            </View>
                          );
                        })
                      )}
                    </View>
                  )}
                </ThemedPanel>
              );
            })
          )}
        </ScrollView>
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  lockIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  accessDeniedTitle: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 8,
    textAlign: "center",
  },
  accessDeniedText: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
    maxWidth: 400,
  },
  metricsRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 12,
  },
  metricCard: {
    flex: 1,
    padding: 10,
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
  },
  metricNumber: {
    fontSize: 20,
    fontWeight: "800",
    marginTop: 4,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 10,
    height: 44,
  },
  searchIcon: {
    fontSize: 16,
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 4,
  },
  filtersScroll: {
    maxHeight: 38,
    marginBottom: 12,
  },
  filterPill: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 18,
    borderWidth: 1,
    marginRight: 8,
    justifyContent: "center",
  },
  filterPillText: {
    fontSize: 13,
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 24,
  },
  emptyContainer: {
    padding: 24,
    alignItems: "center",
  },
  workflowCard: {
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    marginBottom: 14,
  },
  wfHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  wfTitleContainer: {
    flex: 1,
    marginRight: 10,
  },
  wfTitle: {
    fontSize: 17,
    fontWeight: "700",
  },
  wfMemoryText: {
    fontSize: 13,
    marginTop: 3,
  },
  badgePill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: "700",
  },
  badgePillSmall: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  badgeTextSmall: {
    fontSize: 11,
    fontWeight: "600",
  },
  usersInfoSection: {
    backgroundColor: "rgba(0,0,0,0.02)",
    padding: 8,
    borderRadius: 6,
    marginBottom: 10,
    gap: 4,
  },
  userRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  userLabel: {
    fontSize: 12,
    width: 130,
    fontWeight: "500",
  },
  userValue: {
    fontSize: 13,
    fontWeight: "600",
    flex: 1,
  },
  taskAccordionToggle: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 10,
    borderTopWidth: 1,
  },
  taskCountText: {
    fontSize: 14,
    fontWeight: "600",
  },
  tasksListContainer: {
    marginTop: 10,
    gap: 8,
  },
  noTasksText: {
    fontSize: 13,
    fontStyle: "italic",
    paddingVertical: 6,
  },
  taskItemCard: {
    padding: 10,
    borderWidth: 1,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.01)",
  },
  taskItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  taskTitle: {
    fontSize: 14,
    fontWeight: "700",
    flex: 1,
    marginRight: 8,
  },
  taskDescription: {
    fontSize: 13,
    marginBottom: 6,
  },
  actionUserBox: {
    padding: 6,
    borderWidth: 1,
    borderRadius: 6,
    marginBottom: 6,
  },
  actionUserLabel: {
    fontSize: 11,
    marginBottom: 2,
  },
  actionUserName: {
    fontSize: 12,
    fontWeight: "700",
  },
  taskMetaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  taskMetaText: {
    fontSize: 11,
  },
});
